// src/dq/SmeAddRuleDrawer.tsx
// "Add a rule": the SME asks for a NEW rule (field + category + her guidance + related fields of any table).
// The request goes through the same generator and post-processing as discovery (dry run, flags, grouping, dedup,
// challenger, overlap) and comes back as a PREVIEW. Nothing is written to the rulebook until she saves the rules
// she selects.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  sdh_smeRuleCandidates, sdh_smePairEvidence, sdh_smeRuleRequest, sdh_smeRuleSave, sdh_smeRuleDiscard,
  sdh_smeRulePreviewDetail, sdh_smeDiscoverRelated, sdh_smeSuggestRelated, type SmeRelatedItem,
  type SmeCandidateField, type SmeRuleCategory, type SmePairEvidence, type SmeRulePreview, type SmePreviewRule,
  type EnrichedRule,
} from "../api/sdhAPI";
import { RuleDetailModal } from "./SdhReviewWorkspace";

type Step = 1 | 2 | 3 | 4;
type RelSource = "discovered" | "agent" | "sme";
interface Picked {
  field_id: string; keys: string[][]; keysText: string; evidence?: SmePairEvidence | null; measuring?: boolean;
  source: RelSource; why?: string | null; line?: string | null;
}

const pct = (x?: number | null) => (x == null ? "—" : `${x}%`);
const num = (n?: number | null) => (n == null ? "—" : Number(n).toLocaleString());
const errText = (e: any): string => String(e?.detail || e?.message || e || "Request failed");

/** "Purchasing Group (MARC::EKGRP)" - the business name first, the physical name in brackets */
function nameOf(fid: string, lab: Record<string, string>): string {
  const l = lab[String(fid || "").toUpperCase()];
  return l ? `${l} (${fid})` : fid;
}
function keysToText(keys: string[][]): string {
  return (keys || []).map((k) => `${k[0]}=${k[1]}`).join(", ");
}
function textToKeys(t: string): string[][] {
  return String(t || "").split(",").map((p) => p.trim()).filter(Boolean)
    .map((p) => p.split("=").map((x) => x.trim())).filter((p) => p.length === 2 && p[0] && p[1]);
}

export default function SmeAddRuleDrawer(
  { entityId, appNm, initialOwner, onClose, onSaved }:
  { entityId: string; appNm: string; initialOwner?: string; onClose: () => void; onSaved: (msg: string) => void; key?: any }
) {
  const { user } = useAuth();
  const userName: string = (user as any)?.full_name || (user as any)?.email || (user as any)?.user_id || "SME";

  const [step, setStep] = useState<Step>(1);
  const [allFields, setAllFields] = useState<SmeCandidateField[]>([]);
  const [categories, setCategories] = useState<SmeRuleCategory[]>([]);
  const [owner, setOwner] = useState<string>(initialOwner || "");
  const [ownerQuery, setOwnerQuery] = useState<string>(initialOwner || "");
  const [category, setCategory] = useState<string>("auto");
  const [guidance, setGuidance] = useState<string>("");
  const [codes, setCodes] = useState<string>("");
  const [maxRules, setMaxRules] = useState<number>(5);

  const [related, setRelated] = useState<SmeCandidateField[]>([]);
  const [tableFilter, setTableFilter] = useState<string>("");
  const [relQuery, setRelQuery] = useState<string>("");
  const [picked, setPicked] = useState<Picked[]>([]);
  // step 3: "found in your data" (loaded once per field) and "suggested by the agent" (on request)
  const [found, setFound] = useState<SmeRelatedItem[] | null>(null);
  const [foundFor, setFoundFor] = useState<string>("");
  const [foundBusy, setFoundBusy] = useState(false);
  const [suggested, setSuggested] = useState<SmeRelatedItem[] | null>(null);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [relReviewed, setRelReviewed] = useState(false);
  const [relTab, setRelTab] = useState<"found" | "agent" | "own">("found");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<SmeRulePreview | null>(null);
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  const [detail, setDetail] = useState<EnrichedRule | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // the field list (no owner yet) + the categories
  useEffect(() => {
    let live = true;
    sdh_smeRuleCandidates(entityId, appNm)
      .then((r) => { if (live) { setAllFields(r.fields || []); setCategories(r.categories || []); } })
      .catch((e: any) => { if (live) setErr(errText(e)); });
    return () => { live = false; };
  }, [entityId, appNm]);

  // related-field candidates for the chosen owner (with the table's link to it)
  useEffect(() => {
    if (!owner) { setRelated([]); return; }
    let live = true;
    setBusy("Loading related fields…");
    sdh_smeRuleCandidates(entityId, appNm, owner)
      .then((r) => { if (live) setRelated(r.fields || []); })
      .catch((e: any) => { if (live) setErr(errText(e)); })
      .finally(() => { if (live) setBusy(null); });
    return () => { live = false; };
  }, [entityId, appNm, owner]);

  const ownerMatches = useMemo(() => {
    const q = ownerQuery.trim().toUpperCase();
    if (!q || q === owner.toUpperCase()) return [] as SmeCandidateField[];
    return allFields.filter((f) => f.field_id.toUpperCase().includes(q) || String(f.label || "").toUpperCase().includes(q)
      || String(f.description || "").toUpperCase().includes(q)).slice(0, 12);
  }, [ownerQuery, owner, allFields]);

  const ownerRow = useMemo(() => allFields.find((f) => f.field_id === owner) || null, [allFields, owner]);
  // business (logical) names for every field the panel shows: picker lists + the preview's own map
  const lab = useMemo(() => {
    const m: Record<string, string> = {};
    [...allFields, ...related].forEach((f) => { if (f.label) m[f.field_id.toUpperCase()] = String(f.label); });
    Object.entries((preview && preview.labels) || {}).forEach(([k, v]) => { if (v) m[k.toUpperCase()] = String(v); });
    return m;
  }, [allFields, related, preview]);
  const nm = (fid: string): string => nameOf(fid, lab);

  const relShown = useMemo(() => {
    const q = relQuery.trim().toUpperCase();
    return related.filter((f) => (!tableFilter || f.table === tableFilter)
      && (!q || f.field_id.toUpperCase().includes(q) || String(f.label || "").toUpperCase().includes(q)
        || String(f.description || "").toUpperCase().includes(q))).slice(0, 60);
  }, [related, tableFilter, relQuery]);
  const relTables = useMemo(() => Array.from(new Set(related.map((f) => f.table))), [related]);

  const isPicked = (fid: string): boolean => picked.some((p) => p.field_id === fid);
  const togglePick = (f: SmeCandidateField) => {
    setPicked((ps) => isPicked(f.field_id) ? ps.filter((p) => p.field_id !== f.field_id)
      : [...ps, { field_id: f.field_id, keys: f.link?.keys || [], keysText: keysToText(f.link?.keys || []), source: "sme" }]);
  };
  const toPicked = (it: SmeRelatedItem, src: RelSource): Picked => ({
    field_id: it.field_id, keys: it.keys || [], keysText: keysToText(it.keys || []), evidence: it.evidence || null,
    source: src, why: it.why || null, line: it.evidence_line || null });
  const toggleItem = (it: SmeRelatedItem, src: RelSource) =>
    setPicked((ps) => ps.some((p) => p.field_id === it.field_id) ? ps.filter((p) => p.field_id !== it.field_id) : [...ps, toPicked(it, src)]);
  // "found in your data" is loaded as soon as the field is chosen (in the background, while she writes her guidance),
  // ticked by default; she reviews it in step 3. The latest field wins - an older answer is ignored.
  const discoverFor = useRef<string>("");
  useEffect(() => {
    if (!owner || foundFor === owner || discoverFor.current === owner) return;
    discoverFor.current = owner;
    setFoundBusy(true); setFound(null); setSuggested(null);
    sdh_smeDiscoverRelated(entityId, appNm, owner)
      .then((r) => {
        if (discoverFor.current !== owner) return;
        const fs = (r.fields || []).filter((x) => x.measured);
        setFound(fs); setFoundFor(owner);
        setPicked((ps) => [...ps.filter((p) => p.source === "sme"),
          ...fs.filter((x) => !ps.some((p) => p.field_id === x.field_id)).map((x) => toPicked(x, "discovered"))]);
      })
      .catch((e: any) => { if (discoverFor.current === owner) setErr(errText(e)); })
      .finally(() => { if (discoverFor.current === owner) { setFoundBusy(false); discoverFor.current = ""; } });
  }, [owner, foundFor, entityId, appNm]); // eslint-disable-line
  useEffect(() => { if (step === 3) setRelReviewed(true); }, [step]);
  const askAgent = async () => {
    setSuggestBusy(true); setErr(null);
    try {
      const r = await sdh_smeSuggestRelated(entityId, appNm, owner, guidance, category,
        (found || []).map((x) => x.field_id));
      const fs = r.fields || [];
      setSuggested(fs);
      // the agent's suggestions that the data can measure are ticked; she reviews them like the found ones
      setPicked((ps) => [...ps, ...fs.filter((x) => x.measured && !ps.some((p) => p.field_id === x.field_id)).map((x) => toPicked(x, "agent"))]);
    } catch (e: any) { setErr(errText(e)); }
    finally { setSuggestBusy(false); }
  };
  const setKeysText = (fid: string, t: string) =>
    setPicked((ps) => ps.map((p) => p.field_id === fid ? { ...p, keysText: t, keys: textToKeys(t), evidence: null } : p));
  const measure = async (fid: string) => {
    const p = picked.find((x) => x.field_id === fid);
    if (!p || !owner) return;
    setPicked((ps) => ps.map((x) => x.field_id === fid ? { ...x, measuring: true } : x));
    try {
      const ev = await sdh_smePairEvidence(entityId, appNm, owner, fid, p.keys);
      setPicked((ps) => ps.map((x) => x.field_id === fid ? { ...x, evidence: ev, measuring: false } : x));
    } catch (e: any) {
      setPicked((ps) => ps.map((x) => x.field_id === fid ? { ...x, evidence: { ok: false, reason: errText(e) }, measuring: false } : x));
    }
  };

  const generate = async () => {
    setErr(null); setPreview(null); setBusy("Generating and checking the rule(s): generator, dry run, grouping, dedup, challenger… (1–3 min)");
    try {
      const res = await sdh_smeRuleRequest(entityId, appNm, {
        owner_field: owner, category, guidance, max_rules: maxRules, user: userName,
        related_fields: picked.map((p) => ({ field_id: p.field_id, keys: p.keys && p.keys.length ? p.keys : undefined, source: p.source })),
        related_reviewed: relReviewed,
        examples: codes.trim() ? { codes: codes.split(",").map((c) => c.trim()).filter(Boolean) } : undefined,
      });
      setPreview(res);
      const pre: Record<string, boolean> = {};
      (res.rules || []).forEach((r: SmePreviewRule) => { pre[r.rule_id] = !r.already_exists && !r.set_aside && !(r.post_processing || {}).dedup; });
      setChosen(pre);
    } catch (e: any) { setErr(errText(e)); }
    finally { setBusy(null); }
  };

  const openDetails = async (ruleId: string) => {
    if (!preview) return;
    setBusy("Loading the rule details…"); setErr(null);
    try { setDetail(await sdh_smeRulePreviewDetail(entityId, preview.request_id, ruleId)); }
    catch (e: any) { setErr(errText(e)); }
    finally { setBusy(null); }
  };

  const save = async () => {
    if (!preview) return;
    const ids = Object.keys(chosen).filter((k) => chosen[k]);
    if (!ids.length) return;
    setBusy("Saving…"); setErr(null);
    try {
      const r = await sdh_smeRuleSave(entityId, preview.request_id, ids, userName);
      onSaved(`Saved ${r.saved.length} rule(s) to the rulebook as Draft` + (r.skipped.length ? ` (${r.skipped.length} skipped)` : ""));
    } catch (e: any) { setErr(errText(e)); }
    finally { setBusy(null); }
  };
  const discard = async () => {
    if (preview) { try { await sdh_smeRuleDiscard(entityId, preview.request_id); } catch { /* the preview is not saved anyway */ } }
    onClose();
  };

  const canNext1 = !!owner && !!category;
  // EXPLORE is automatic: when she gives no rule type, no codes and no related field (her text, if any, only
  // steers), the agent explores every candidate, related field and rule type. Anything specific = targeted.
  const explore = category === "auto" && !picked.some((p) => p.source === "sme") && !codes.trim();
  const canGenerate = canNext1 && (explore || guidance.trim().length >= 10);
  // guidance that names ANOTHER field's code (e.g. "DISPO" while the field is BESKZ): the rule must belong to the
  // field it checks - warn before generating
  const otherFieldsNamed = useMemo(() => {
    const words = new Set((guidance.toUpperCase().match(/[A-Z][A-Z0-9_-]{2,}/g) || []));
    const ownCol = owner.split("::")[1] || "";
    const hits = allFields.filter((f) => f.field_id !== owner && words.has(f.column.toUpperCase()) && f.column.toUpperCase() !== ownCol.toUpperCase());
    return Array.from(new Set(hits.map((f) => f.field_id))).slice(0, 4);
  }, [guidance, owner, allFields]);
  const catDef = categories.find((c) => c.key === category);

  return (
    <div className="sar-scrim">
      <style>{CSS}</style>
      <div className="sar-panel" role="dialog" aria-label="Add a rule">
        <div className="sar-head">
          <div>
            <div className="sar-title">Add a rule</div>
            <div className="sar-sub">Describe the rule you need. It is generated and checked against your data like every discovered rule, then shown to you. Nothing is saved until you choose.</div>
          </div>
          <button className="sar-x" onClick={discard} aria-label="Close">×</button>
        </div>

        {!preview && (
          <div className="sar-steps">
            {(["Field & type", "Your guidance", "Related fields", "Review"] as string[]).map((s, i) => (
              <button key={s} className={"sar-step" + (step === i + 1 ? " on" : "") + (step > i + 1 ? " done" : "")}
                onClick={() => { if (i + 1 <= step || (i + 1 === 2 && canNext1) || (i + 1 >= 3 && canNext1)) setStep((i + 1) as Step); }}>
                <span>{i + 1}</span>{s}
              </button>
            ))}
          </div>
        )}

        <div className="sar-body">
          {err && <div className="sar-err">{err}</div>}
          {busy && <div className="sar-busy"><span className="sar-spin" />{busy}</div>}

          {!preview && step === 1 && (
            <div>
              <label className="sar-lab">Field the rule is about</label>
              {initialOwner && owner === initialOwner && (
                <div className="sar-hint">Starting from the field selected in the list: <b>{nm(owner)}</b>. Change it if your rule is about another field.</div>
              )}
              <input className="sar-in" placeholder="Search a field by name or code, e.g. Minimum order or AUMNG" value={ownerQuery}
                onChange={(e: any) => { setOwnerQuery(e.target.value); if (e.target.value !== owner) setOwner(""); }} />
              {ownerMatches.length > 0 && (
                <div className="sar-list">
                  {ownerMatches.map((f) => (
                    <button key={f.field_id} className="sar-li" onClick={() => { setOwner(f.field_id); setOwnerQuery(f.field_id); setPicked([]); }}>
                      <b>{f.label || f.field_id}</b><span>{f.label ? f.field_id : ""}{f.description ? ` · ${f.description}` : ""}</span><em>{pct(f.fill_pct)} filled</em>
                    </button>
                  ))}
                </div>
              )}
              {ownerRow && (
                <div className="sar-card">
                  <b>{nm(ownerRow.field_id)}</b> · {pct(ownerRow.fill_pct)} filled · {num(ownerRow.distinct)} distinct
                  {ownerRow.top_values?.length ? <div className="sar-vals">{ownerRow.top_values.map((v) => <span key={v.value}>{v.value} <i>{num(v.count)}</i></span>)}</div> : null}
                </div>
              )}
              <label className="sar-lab">Type of rule</label>
              <div className="sar-cats">
                {categories.map((c) => (
                  <button key={c.key} className={"sar-cat" + (category === c.key ? " on" : "")} onClick={() => setCategory(c.key)}>
                    <b>{c.label}</b><span>{c.shape}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!preview && step === 2 && (
            <div>
              <label className="sar-lab">{explore ? "What matters to you about this field? (optional, any words)" : "What should the rule check? (your words)"}</label>
              <textarea className="sar-ta" rows={6} value={guidance} onChange={(e: any) => setGuidance(e.target.value)}
                placeholder={explore ? "e.g. orders fail sometimes / check what should be filled / anything about plants" :
                  "e.g. When a minimum order quantity is maintained, the delivering plant must have a plant view for the material."} />
              <label className="sar-lab">Codes or values that matter (optional, comma separated)</label>
              <input className="sar-in" value={codes} onChange={(e: any) => setCodes(e.target.value)} placeholder="e.g. ZL, PD" />
              <label className="sar-lab">At most</label>
              <select className="sar-in sar-sm" value={maxRules} onChange={(e: any) => setMaxRules(Number(e.target.value))}>
                {(explore ? [1, 2, 3, 4, 5, 6, 7, 8] : [1, 2, 3, 4, 5]).map((n) => <option key={n} value={n}>{n} rule{n > 1 ? "s" : ""}</option>)}
              </select>
            </div>
          )}

          {!preview && step === 3 && (
            <div>
              <div className="sar-hint">Optional. The agent considers these fields together with <b>{owner ? nm(owner) : "the field"}</b>. Keep what belongs, ask the agent for more, or add your own.</div>

              <div className="sar-selbar">
                <span className="sar-k" style={{ minWidth: 0 }}>Selected ({picked.length})</span>
                {picked.length === 0 && <span className="sar-d">none yet - the agent then picks related fields itself</span>}
                {picked.map((p) => (
                  <span key={p.field_id} className={"sar-sel " + p.source} title={p.why || ""}>
                    {nm(p.field_id)}<i>{p.source === "discovered" ? "found" : p.source === "agent" ? "agent" : "yours"}</i>
                    <button aria-label="remove" onClick={() => setPicked((ps) => ps.filter((x) => x.field_id !== p.field_id))}>×</button>
                  </span>
                ))}
              </div>

              <div className="sar-tabs">
                <button className={relTab === "found" ? "on" : ""} onClick={() => setRelTab("found")}>
                  Found in your data <span>{picked.filter((p) => p.source === "discovered").length}/{(found || []).length}</span>{foundBusy && <span className="sar-spin sm" />}</button>
                <button className={relTab === "agent" ? "on" : ""} onClick={() => setRelTab("agent")}>
                  Suggested by the agent <span>{picked.filter((p) => p.source === "agent").length}/{(suggested || []).length}</span>{suggestBusy && <span className="sar-spin sm" />}</button>
                <button className={relTab === "own" ? "on" : ""} onClick={() => setRelTab("own")}>
                  Add your own <span>{picked.filter((p) => p.source === "sme").length}</span></button>
              </div>

              <div className="sar-tabbody">
                {relTab === "found" && (
                  <div>
                    <div className="sar-d" style={{ marginBottom: 6 }}>Measured in the data: the partners of the field's own patterns, numeric fields that move with it row by row, strong associations. Kept from the last time this field was opened.</div>
                    {foundBusy && <div className="sar-busy"><span className="sar-spin" />Looking at the data for related fields… (the first time for a field can take up to a minute; then it is kept)</div>}
                    {!foundBusy && found && !found.length && <div className="sar-d">Nothing stands out in the data for this field - try the agent's suggestions.</div>}
                    {(found || []).map((it) => <RelRow key={it.field_id} it={it} nm={nm} checked={isPicked(it.field_id)}
                      onToggle={() => toggleItem(it, "discovered")} chip={it.why || "found"} />)}
                  </div>
                )}
                {relTab === "agent" && (
                  <div>
                    <div className="sar-row" style={{ marginTop: 0 }}>
                      <span className="sar-d" style={{ flex: 1 }}>The agent reads the meaning of every field{guidance.trim() ? " and your guidance" : ""} and suggests fields with a business dependency; each one is then joined and measured.</span>
                      <button className="sar-btn out" disabled={!owner || suggestBusy} onClick={askAgent}>
                        {suggestBusy ? "Asking…" : suggested ? "Ask again" : "✨ Ask the agent"}</button>
                    </div>
                    {suggestBusy && <div className="sar-busy"><span className="sar-spin" />Reading the field catalogue, then measuring each suggestion… (about 20 s)</div>}
                    {!suggestBusy && suggested && !suggested.length && <div className="sar-d">The agent had no further suggestion.</div>}
                    {(suggested || []).map((it) => <RelRow key={it.field_id} it={it} nm={nm} checked={isPicked(it.field_id)}
                      onToggle={() => toggleItem(it, "agent")} chip="agent" reason={it.why} />)}
                  </div>
                )}
                {relTab === "own" && (
                  <div>
                    <div className="sar-d" style={{ marginBottom: 6 }}>Any field, any table. A field you add makes the request targeted.</div>
                {picked.filter((p) => p.source === "sme").length > 0 && (
                  <div className="sar-picked">
                    {picked.filter((p) => p.source === "sme").map((p) => (
                      <div key={p.field_id} className="sar-pk">
                        <div className="sar-pk-h"><b>{nm(p.field_id)}</b>
                          <button className="sar-link" onClick={() => setPicked((ps) => ps.filter((x) => x.field_id !== p.field_id))}>remove</button></div>
                        {p.field_id.split("::")[0] !== owner.split("::")[0] && (
                          <div className="sar-row"><span className="sar-k">join ({owner.split("::")[0]} = {p.field_id.split("::")[0]})</span>
                            <input className="sar-in" value={p.keysText} onChange={(e: any) => setKeysText(p.field_id, e.target.value)} placeholder="MATNR=MATNR, DWERK=WERKS" /></div>
                        )}
                        <button className="sar-btn out" disabled={!!p.measuring} onClick={() => measure(p.field_id)}>{p.measuring ? "Measuring…" : "Measure"}</button>
                        {p.evidence && (p.evidence.ok ? (
                          <div className="sar-ev">
                            <div>{p.evidence.how} · association {p.evidence.association} (0 none … 1 full)</div>
                            <table><thead><tr><th>{lab[owner.toUpperCase()] || owner.split("::")[1]}</th><th>rows</th><th>{lab[p.field_id.toUpperCase()] || p.field_id.split("::")[1]} filled</th><th>most frequent</th></tr></thead>
                              <tbody>{(p.evidence.by_owner_value || []).slice(0, 6).map((r) => (
                                <tr key={r.owner_value}><td>{r.owner_value}</td><td>{num(r.rows)}</td><td>{pct(r.related_filled_pct)}</td>
                                  <td>{r.related_top ? `${r.related_top} (${pct(r.related_top_pct)})` : "—"}</td></tr>))}</tbody></table>
                          </div>) : <div className="sar-err">{p.evidence.reason}</div>)}
                      </div>
                    ))}
                  </div>
                )}
                <div className="sar-row">
                  <select className="sar-in sar-sm" value={tableFilter} onChange={(e: any) => setTableFilter(e.target.value)}>
                    <option value="">All tables</option>
                    {relTables.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input className="sar-in" placeholder="Search a field by name or code" value={relQuery} onChange={(e: any) => setRelQuery(e.target.value)} />
                </div>
                {related.length === 0 && <div className="sar-d">Loading the field list…</div>}
                {related.length > 0 && (
                  <div className="sar-cands">
                    {relShown.filter((f) => !(found || []).some((x) => x.field_id === f.field_id) && !(suggested || []).some((x) => x.field_id === f.field_id)).map((f) => (
                      <label key={f.field_id} className={"sar-cand" + (isPicked(f.field_id) ? " on" : "")}>
                        <input type="checkbox" checked={isPicked(f.field_id)} onChange={() => togglePick(f)} />
                        <div><b>{nm(f.field_id)}</b> <span className="sar-rel">{f.link?.relation === "same_table" ? "same table"
                          : f.link?.relation === "none" ? "no measured join" : `${f.link?.relation} · ${keysToText(f.link?.keys || [])}${f.link?.match_pct != null ? ` · ${f.link.match_pct}% match` : ""}`}</span>
                          <div className="sar-d">{f.description || ""} · {pct(f.fill_pct)} filled{f.top_values?.length ? " · " + f.top_values.slice(0, 4).map((v) => v.value).join(", ") : ""}</div></div>
                      </label>
                    ))}
                  </div>
                )}
                                </div>
                )}
              </div>

              <div className="sar-sum">{explore ? "The agent explores with the selected fields." : "Targeted request - your own field(s), type or codes."}</div>
            </div>
          )}

          {!preview && step === 4 && (
            <div className="sar-card">
              <div><span className="sar-k">Field</span> {nm(owner)}</div>
              <div><span className="sar-k">Type</span> {catDef?.label || category}</div>
              <div><span className="sar-k">Guidance</span> {guidance || "—"}</div>
              {codes && <div><span className="sar-k">Codes</span> {codes}</div>}
              <div><span className="sar-k">Related</span> {picked.length ? picked.map((p) => nm(p.field_id) + ` [${p.source === "discovered" ? "found" : p.source === "agent" ? "agent" : "yours"}]` + (p.keys.length ? ` · join ${keysToText(p.keys)}` : "")).join("; ") : (relReviewed ? "none" : "not reviewed - the agent picks related fields itself")}</div>
              <div><span className="sar-k">At most</span> {maxRules}</div>
              <div><span className="sar-k">Mode</span> {explore ? "no type, codes or related fields given - the agent explores everything (your text only steers it)" : "targeted request (your type / codes / related fields)"}</div>
              {otherFieldsNamed.length > 0 && (
                <div className="sar-err">Your guidance names {otherFieldsNamed.map((f) => nm(f)).join(", ")} - a rule belongs to the field it checks. If the rule is about that field, go back to step 1 and choose it.</div>
              )}
              {!canGenerate && <div className="sar-err">{explore ? "Choose a field." : "Write at least a sentence of guidance - or leave type, codes and related fields empty and the agent explores."}</div>}
            </div>
          )}

          {preview && (
            <div>
              {!preview.rules.length && (
                <div className="sar-card"><b>No rule was produced.</b>
                  <div>{preview.answer?.declined_reason ? `The agent's reason: ${preview.answer.declined_reason}` : "The agent returned no rule for this request."}</div></div>
              )}
              {preview.rules.map((r) => <PreviewCard key={r.rule_id} r={r} checked={!!chosen[r.rule_id]} nm={nm}
                onToggle={() => setChosen((c) => ({ ...c, [r.rule_id]: !c[r.rule_id] }))}
                onDetails={() => openDetails(r.rule_id)} />)}
              {(preview.auto_related || []).length > 0 && (
                <div className="sar-d" style={{ margin: "6px 0" }}><b>Related fields the agent also looked at</b> (ranked by measured association): {(preview.auto_related || []).map((f) => nm(f)).join(" · ")}</div>
              )}
              <details className="sar-stages"><summary>How it was checked</summary><pre>{JSON.stringify(preview.stages, null, 2)}</pre></details>
            </div>
          )}
        </div>

        <div className="sar-foot">
          {!preview ? (
            <>
              <button className="sar-btn out" onClick={discard}>Cancel</button>
              <div style={{ flex: 1 }} />
              {step > 1 && <button className="sar-btn out" onClick={() => setStep((step - 1) as Step)}>Back</button>}
              {step < 4 && <button className="sar-btn" disabled={step === 1 ? !canNext1 : false} onClick={() => setStep((step + 1) as Step)}>Next</button>}
              {step === 4 && <button className="sar-btn" disabled={!canGenerate || !!busy} onClick={generate}>Generate & check</button>}
            </>
          ) : (
            <>
              <button className="sar-btn out" onClick={discard}>Discard</button>
              <button className="sar-btn out" onClick={() => { setPreview(null); setStep(2); }}>Change the request</button>
              <div style={{ flex: 1 }} />
              <button className="sar-btn" disabled={!!busy || !Object.values(chosen).some(Boolean)} onClick={save}>
                Save selected to the rulebook</button>
            </>
          )}
        </div>
      </div>
      {detail && (
        <div className="sar-detail">
          <RuleDetailModal entityId={entityId} appNm={appNm} rule={detail}
            onClose={() => setDetail(null)}
            onDone={(m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2600); }}
            previewFooter={<>
              <span className="sar-d" style={{ flex: 1 }}>Preview - this rule is not in the rulebook yet. Tick it in the panel and "Save selected" to add it.</span>
              <button className="dqc-btn out" onClick={() => setDetail(null)}>Back to the preview</button>
            </>} />
          {flash && <div className="sar-flash">{flash}</div>}
        </div>
      )}
    </div>
  );
}

function RelRow({ it, nm, checked, onToggle, chip, reason }:
  { it: SmeRelatedItem; nm: (f: string) => string; checked: boolean; onToggle: () => void; chip: string; reason?: string | null; key?: any }) {
  return (
    <label className={"sar-cand" + (checked ? " on" : "")}>
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <div style={{ flex: 1 }}>
        <b>{nm(it.field_id)}</b> <span className="sar-chip">{chip}</span>
        {it.how && <span className="sar-rel">{it.how}</span>}
        {reason && <div className="sar-d"><i>{reason}</i></div>}
        <div className="sar-d">{it.measured ? it.evidence_line : "not measurable on the data: " + (it.evidence_line || "")}</div>
      </div>
    </label>
  );
}

function PreviewCard({ r, checked, onToggle, onDetails, nm }:
  { r: SmePreviewRule; checked: boolean; onToggle: () => void; onDetails: () => void; nm: (f: string) => string; key?: any }) {
  const ia = r.impact || {};
  const exec = !!ia.executed;
  const dup = (r.possible_duplicate_of || [])[0];
  const pp = r.post_processing || {};
  const blocked = !!r.already_exists;
  return (
    <div className={"sar-rule" + (checked ? " on" : "")}>
      <div className="sar-rule-h">
        <input type="checkbox" checked={checked} disabled={blocked} onChange={onToggle} />
        <div style={{ flex: 1 }}>
          <b>{r.title || r.rule_id}</b> <span className="sar-id">{r.rule_id}</span>
          {r.business_summary && <div className="sar-d">{r.business_summary}</div>}
        </div>
        <button className="sar-btn out sar-det" onClick={onDetails}>Details</button>
      </div>
      <div className="sar-logic">{r.logic_text || "(no executable logic)"}</div>
      {fieldsOf(r).length > 0 && <div className="sar-d"><b>Fields:</b> {fieldsOf(r).map((f) => nm(f)).join(" · ")}</div>}
      <div className="sar-tags">
        {blocked && <span className="sar-tag warn">Already a stored rule ({r.already_exists})</span>}
        {exec ? <span className="sar-tag">{Number(ia.violations || 0).toLocaleString()} of {Number(ia.applicable_rows || 0).toLocaleString()} rows fail
          {ia.violation_rate != null ? ` (${(Number(ia.violation_rate) * 100).toFixed(1)}%)` : ""}</span>
          : <span className="sar-tag warn">Not measured{ia.note ? `: ${ia.note}` : ""}</span>}
        {r.set_aside && <span className="sar-tag warn">Would be set aside: {r.set_aside_label || "flagged"}</span>}
        {(r.review_flags || []).map((f) => <span key={f} className="sar-tag">{f.replace(/_/g, " ")}</span>)}
        {r.challenge?.verdict && <span className={"sar-tag" + (String(r.challenge.verdict).toLowerCase() === "keep" ? " ok" : " warn")}>Challenger: {r.challenge.verdict}</span>}
        {dup && <span className="sar-tag warn">Possible duplicate of {dup.rule_id}{dup.shared_share != null ? ` (${Math.round(Number(dup.shared_share) * 100)}% shared failures)` : ""}</span>}
        {pp.dedup && <span className="sar-tag warn">{pp.dedup}</span>}
      </div>
      {r.challenge?.rationale && <div className="sar-d"><b>Challenger:</b> {r.challenge.rationale}</div>}
      {r.set_aside_action && <div className="sar-d"><b>Recommended:</b> {r.set_aside_action}</div>}
      {r.g5 && <div className="sar-d"><b>Evidence check:</b> {r.g5}</div>}
      {(r.sample_failing || []).length > 0 && (
        <details><summary>Failing examples</summary>
          <table className="sar-tbl"><thead><tr>{Object.keys((r.sample_failing || [])[0] || {}).map((k) => <th key={k}>{k}</th>)}</tr></thead>
            <tbody>{(r.sample_failing || []).map((row, i) => (
              <tr key={i}>{Object.keys((r.sample_failing || [])[0] || {}).map((k) => <td key={k}>{String(row[k] ?? "")}</td>)}</tr>))}</tbody></table>
        </details>
      )}
    </div>
  );
}

/** the fields a rule's logic uses, in order of appearance */
function fieldsOf(r: SmePreviewRule): string[] {
  const out: string[] = [];
  const add = (v: any) => { if (typeof v === "string" && v.includes("::") && !out.includes(v)) out.push(v); };
  const walk = (x: any): void => {
    if (!x || typeof x !== "object") return;
    if (Array.isArray(x)) { x.forEach(walk); return; }
    add(x.field); add(x.field2);
    (x.match || []).forEach((p: any) => (p || []).forEach(add));
    Object.keys(x).forEach((k: string) => { if (k !== "match" && typeof x[k] === "object") walk(x[k]); });
  };
  walk(r.logic);
  return out;
}

const CSS = `
.sar-scrim{position:fixed;inset:0;z-index:2600;background:rgba(14,36,54,.42);display:flex;justify-content:flex-end}
.sar-panel{width:min(880px,96vw);height:100%;background:#fff;display:flex;flex-direction:column;box-shadow:-8px 0 30px rgba(0,0,0,.18);font-family:inherit}
.sar-head{display:flex;gap:12px;align-items:flex-start;padding:18px 22px 10px;border-bottom:1px solid #e5e8eb}
.sar-title{font-size:18px;font-weight:700;color:#1d2a35}.sar-sub{font-size:12.5px;color:#5b6b78;margin-top:3px;max-width:680px}
.sar-x{margin-left:auto;all:unset;cursor:pointer;font-size:24px;line-height:1;color:#5b6b78;padding:0 6px}
.sar-steps{display:flex;gap:6px;padding:10px 22px;border-bottom:1px solid #eef0f2}
.sar-step{all:unset;cursor:pointer;font-size:12.5px;color:#5b6b78;padding:6px 10px;border-radius:16px;display:flex;gap:6px;align-items:center}
.sar-step span{display:inline-flex;width:18px;height:18px;border-radius:9px;background:#e9ecef;align-items:center;justify-content:center;font-size:11px}
.sar-step.on{background:#f3e8ff;color:#5b0090;font-weight:600}.sar-step.on span,.sar-step.done span{background:#7500C0;color:#fff}
.sar-body{flex:1;overflow:auto;padding:16px 22px}
.sar-foot{display:flex;gap:8px;padding:12px 22px;border-top:1px solid #e5e8eb;background:#fafbfc}
.sar-lab{display:block;font-size:12px;font-weight:600;color:#33424f;margin:14px 0 6px}
.sar-in{width:100%;box-sizing:border-box;border:1px solid #cfd6dc;border-radius:7px;padding:8px 10px;font:inherit;font-size:13px}
.sar-sm{width:auto;min-width:140px}.sar-ta{width:100%;box-sizing:border-box;border:1px solid #cfd6dc;border-radius:7px;padding:9px 10px;font:inherit;font-size:13px}
.sar-list{border:1px solid #e1e5e9;border-radius:7px;margin-top:4px;max-height:240px;overflow:auto}
.sar-li{all:unset;cursor:pointer;display:flex;gap:10px;padding:7px 10px;font-size:12.5px;width:100%;box-sizing:border-box;border-bottom:1px solid #f1f3f5}
.sar-li:hover{background:#f7f2fc}.sar-li span{color:#5b6b78;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.sar-li em{color:#8a97a3;font-style:normal}
.sar-card{border:1px solid #e1e5e9;border-radius:9px;padding:10px 12px;margin-top:10px;font-size:13px;line-height:1.6}
.sar-vals{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}.sar-vals span{background:#f1f3f5;border-radius:10px;padding:2px 8px;font-size:12px}.sar-vals i{color:#8a97a3;font-style:normal}
.sar-cats{display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:8px}
.sar-cat{all:unset;cursor:pointer;border:1px solid #e1e5e9;border-radius:9px;padding:9px 11px;display:flex;flex-direction:column;gap:3px}
.sar-cat b{font-size:13px;color:#1d2a35}.sar-cat span{font-size:11.5px;color:#5b6b78}.sar-cat.on{border-color:#7500C0;background:#faf5ff}
.sar-hint{font-size:12.5px;color:#5b6b78;margin-bottom:10px}
.sar-row{display:flex;gap:8px;align-items:center;margin:6px 0}
.sar-cands{border:1px solid #e1e5e9;border-radius:9px;max-height:330px;overflow:auto;margin-top:8px}
.sar-cand{display:flex;gap:9px;padding:7px 10px;border-bottom:1px solid #f1f3f5;font-size:12.5px;cursor:pointer}.sar-cand.on{background:#faf5ff}
.sar-rel{color:#7500C0;font-size:11.5px;margin-left:6px}.sar-d{color:#5b6b78;font-size:12px;margin-top:2px}
.sar-picked{display:flex;flex-direction:column;gap:8px;margin:8px 0}
.sar-pk{border:1px solid #d9c2f0;border-radius:9px;padding:8px 10px;background:#fcf9ff}
.sar-pk-h{display:flex;justify-content:space-between;font-size:13px;margin-bottom:4px}
.sar-k{font-size:11.5px;color:#8a97a3;min-width:120px;display:inline-block}
.sar-ev{font-size:12px;margin-top:6px}.sar-ev table,.sar-tbl{border-collapse:collapse;margin-top:4px;font-size:11.5px}
.sar-ev th,.sar-ev td,.sar-tbl th,.sar-tbl td{border:1px solid #e5e8eb;padding:3px 7px;text-align:left}
.sar-btn{font-weight:600;font-size:13px;border-radius:8px;padding:8px 15px;cursor:pointer;border:1px solid transparent;font-family:inherit;background:#7500C0;color:#fff}
.sar-btn:disabled{opacity:.45;cursor:default}.sar-btn.out{background:#fff;border-color:#cfd6dc;color:#33424f}
.sar-link{all:unset;cursor:pointer;color:#7500C0;font-size:12px;text-decoration:underline}
.sar-err{background:#fff1f0;border:1px solid #f3c5c0;color:#9b2c20;border-radius:7px;padding:8px 10px;font-size:12.5px;margin:6px 0}
.sar-busy{display:flex;gap:9px;align-items:center;background:#f7f2fc;border-radius:7px;padding:9px 11px;font-size:12.5px;color:#4a1d6e;margin-bottom:10px}
.sar-spin{width:14px;height:14px;border-radius:7px;border:2px solid #d9c2f0;border-top-color:#7500C0;animation:sarspin 1s linear infinite}
@keyframes sarspin{to{transform:rotate(360deg)}}
.sar-rule{border:1px solid #e1e5e9;border-radius:10px;padding:10px 12px;margin-bottom:10px}.sar-rule.on{border-color:#7500C0}
.sar-rule-h{display:flex;gap:9px;align-items:flex-start;font-size:13.5px}.sar-id{color:#8a97a3;font-size:11.5px}
.sar-logic{font-family:ui-monospace,Consolas,monospace;font-size:12px;background:#f6f8fa;border-radius:6px;padding:7px 9px;margin:7px 0;white-space:pre-wrap}
.sar-tags{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:5px}.sar-tag{font-size:11.5px;border-radius:10px;padding:2px 8px;background:#eef2f5;color:#33424f}
.sar-tag.ok{background:#e6f6ec;color:#1e6b3a}.sar-tag.warn{background:#fff4e5;color:#8a4b00}
.sar-det{padding:5px 11px;font-size:12px}
.sar-detail{position:fixed;inset:0;z-index:2700}
.sar-flash{position:fixed;bottom:22px;left:50%;transform:translateX(-50%);background:#1d2a35;color:#fff;padding:8px 14px;border-radius:8px;font-size:12.5px;z-index:2800}
.sar-explore{display:flex;gap:10px;align-items:flex-start;border:1px solid #d9c2f0;background:#fcf9ff;border-radius:9px;padding:10px 12px;margin-top:12px;font-size:13px;cursor:pointer}
.sar-sec{border:1px solid #e1e5e9;border-radius:10px;padding:10px 12px;margin-top:10px}
.sar-selbar{display:flex;flex-wrap:wrap;gap:6px;align-items:center;border:1px solid #e1e5e9;border-radius:10px;padding:8px 10px;margin-top:8px;background:#fafbfc;max-height:92px;overflow:auto}
.sar-sel{display:inline-flex;align-items:center;gap:5px;font-size:12px;border-radius:14px;padding:3px 4px 3px 10px;border:1px solid #d9c2f0;background:#fff}
.sar-sel i{font-style:normal;font-size:10.5px;border-radius:8px;padding:0 6px;background:#f3e8ff;color:#5b0090}
.sar-sel.agent{border-color:#bcd7f5}.sar-sel.agent i{background:#e8f1fc;color:#1f4f8a}
.sar-sel.sme{border-color:#cfe6d6}.sar-sel.sme i{background:#e6f6ec;color:#1e6b3a}
.sar-sel button{all:unset;cursor:pointer;font-size:14px;line-height:1;color:#8a97a3;padding:0 5px}
.sar-tabs{display:flex;gap:4px;margin-top:10px;border-bottom:1px solid #e1e5e9}
.sar-tabs button{all:unset;cursor:pointer;font-size:13px;color:#5b6b78;padding:8px 12px;border-bottom:2px solid transparent;display:flex;gap:6px;align-items:center}
.sar-tabs button span{font-size:11px;background:#eef2f5;border-radius:9px;padding:0 7px;color:#33424f}
.sar-tabs button.on{color:#5b0090;border-bottom-color:#7500C0;font-weight:600}
.sar-tabbody{border:1px solid #e1e5e9;border-top:none;border-radius:0 0 10px 10px;padding:10px 12px;max-height:46vh;overflow:auto}
.sar-spin.sm{width:10px;height:10px;border-width:2px}
.sar-sec-h{display:flex;gap:10px;align-items:center;margin-bottom:6px;font-size:13px;flex-wrap:wrap}
.sar-chip{font-size:11px;border-radius:9px;padding:1px 7px;background:#f3e8ff;color:#5b0090;margin-left:4px}
.sar-sum{margin-top:10px;font-size:12.5px;color:#33424f;background:#f6f8fa;border-radius:8px;padding:8px 10px}
.sar-stages{margin-top:8px;font-size:12px}.sar-stages pre{background:#f6f8fa;padding:8px;border-radius:6px;overflow:auto}
`;
